import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Stack,
  IconButton,
  Divider,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getInvoiceDetailsById, payInvoice } from "../../services/api/invoice";
import { showToast } from "../../store/slices/toastSlice";
import { useDispatch } from "react-redux";

const PayInvoiceDialog = ({
  open,
  onClose,
  invoiceId,
  invoiceAmount,
  rentAmount,
}) => {
  const dispatch = useDispatch();
  const [selectedPaymentOption, setSelectedPaymentOption] = useState("total");
  const [otherAmount, setOtherAmount] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("");
  const [currentOutstanding, setCurrentOutstanding] = useState(invoiceAmount); // initial fallback

  useEffect(() => {
    const fetchInvoice = async () => {
      if (open) {
        try {
          const invoice = await getInvoiceDetailsById(invoiceId);
          setCurrentOutstanding(
            invoice.data.invoice_status == "PARTIAL_PAID"
              ? invoice.data.overdue_amount
              : invoice.data.invoice_amount
          );
          setInvoiceStatus(invoice.data.invoice_status);
        } catch (error) {
          dispatch(showToast({ message: error.message, severity: "error" }));
        }

        setSelectedPaymentOption("total");
        setOtherAmount("");
      }
    };

    fetchInvoice();
  }, [open, invoiceId]);

  const handlePayNow = async (e) => {
    e.preventDefault();

    let amountToPay = 0;

    if (selectedPaymentOption === "total") {
      amountToPay = currentOutstanding;
    } else if (selectedPaymentOption === "rent") {
      amountToPay = rentAmount;
    } else {
      amountToPay = parseFloat(otherAmount);
    }

    try {
      const response = await payInvoice(invoiceId, amountToPay);
      if (response.responseCode === 200) {
        dispatch(showToast({ message: response.message, severity: "success" }));
        localStorage.setItem("generateInvoiceSuccess", response.message);
        window.location.reload();
      }
    } catch (error) {
      dispatch(showToast({ message: error.message, severity: "error" }));
    }

    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: "#ffedbb" }}>Pay Invoice</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>

      <Divider />

      <DialogContent sx={{ backgroundColor: "#f9f9f9" }}>
        {invoiceStatus == "PAID" ? (
          <>
            <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
              Invoice Fully Paid
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
              Congratulations! The invoice has been fully paid and there are no
              overdue amounts.
            </Typography>
          </>
        ) : (
          <>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={selectedPaymentOption}
                onChange={(e) => setSelectedPaymentOption(e.target.value)}
              >
                <FormControlLabel
                  value="total"
                  control={<Radio />}
                  label={`Total Outstanding — ₹${parseFloat(
                    currentOutstanding
                  ).toFixed(2)}`}
                />
                <FormControlLabel
                  value="rent"
                  control={<Radio />}
                  label={`Current Rent — ₹${parseFloat(rentAmount).toFixed(2)}`}
                />
                <FormControlLabel
                  value="other"
                  control={<Radio />}
                  label={
                    <Stack direction="row" spacing={2} alignItems="center">
                      <span>Other Amount</span>
                      {selectedPaymentOption === "other" && (
                        <TextField
                          type="text"
                          size="large"
                          value={otherAmount}
                          onChange={(e) => setOtherAmount(e.target.value)}
                          placeholder="Enter amount"
                        />
                      )}
                    </Stack>
                  }
                />
              </RadioGroup>
            </FormControl>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="text" color="error">
          Cancel
        </Button>

        {invoiceStatus == "PAID" ? (
          ""
        ) : (
          <Button
            onClick={handlePayNow}
            variant="contained"
            color="primary"
            disabled={
              selectedPaymentOption === "other" &&
              (!otherAmount || parseFloat(otherAmount) <= 0)
            }
          >
            Pay Now
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PayInvoiceDialog;
